import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Clock, Briefcase, FileText, Building2, Filter, Pin } from "lucide-react";

interface MessageContact {
  id: string;
  name: string;
  avatar?: string;
  type: "team" | "client" | "lead" | "prime-contractor" | "subcontractor" | "business-contact";
  lastMessage?: {
    content: string;
    timestamp: string;
    isUnread: boolean;
  };
  unreadCount?: number;
  organizationId?: string;
  isPinned?: boolean;
  reference?: {
    type: "bid" | "business" | "opportunity" | "application";
    id: string;
    name: string;
  };
}

interface MessageListProps {
  contacts: MessageContact[];
  onSelectContact: (contactId: string) => void;
  selectedContactId?: string;
  onNewMessage: () => void;
}

// Helper function to get reference info for display
const getReferenceInfo = (type: string) => {
  switch (type) {
    case "opportunity":
      return {
        icon: Briefcase,
        label: "Opportunity",
        color: "bg-blue-50 text-blue-700 border-blue-200",
      };
    case "application":
      return {
        icon: FileText,
        label: "Application",
        color: "bg-green-50 text-green-700 border-green-200",
      };
    case "bid":
      return {
        icon: FileText,
        label: "Bid",
        color: "bg-purple-50 text-purple-700 border-purple-200",
      };
    case "business":
      return {
        icon: Building2,
        label: "Business",
        color: "bg-orange-50 text-orange-700 border-orange-200",
      };
    default:
      return null;
  }
};

export function MessageList({
  contacts,
  onSelectContact,
  selectedContactId,
  onNewMessage,
}: MessageListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [referenceFilter, setReferenceFilter] = useState<string>("all");

  const filteredContacts = contacts.filter((contact) => {
    // Search filter
    const matchesSearch =
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.reference?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      false;

    // Reference type filter
    const matchesReference =
      referenceFilter === "all" ||
      (referenceFilter === "direct" && !contact.reference) ||
      contact.reference?.type === referenceFilter;

    return matchesSearch && matchesReference;
  });

  return (
    <div className="flex h-full w-full max-w-full flex-col overflow-hidden border-r bg-white">
      <div className="border-b p-4">
        <h2 className="mb-4 text-lg font-semibold">Messages</h2>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
            <Input
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
            <Select value={referenceFilter} onValueChange={setReferenceFilter}>
              <SelectTrigger className="pl-9">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Conversations</SelectItem>
                <SelectItem value="direct">Direct Messages</SelectItem>
                <SelectItem value="opportunity">Opportunities</SelectItem>
                <SelectItem value="application">Applications</SelectItem>
                <SelectItem value="bid">Bids</SelectItem>
                <SelectItem value="business">Business</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={onNewMessage} className="mt-3 w-full">
          <Plus className="mr-2 h-4 w-4" /> New Message
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="divide-y">
          {filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => (
              <div
                key={contact.id}
                className={`cursor-pointer p-4 hover:bg-gray-50 ${selectedContactId === contact.id ? "bg-blue-50" : ""}`}
                onClick={() => onSelectContact(contact.id)}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="mt-0.5 h-10 w-10 flex-shrink-0">
                    <AvatarImage src={contact.avatar} />
                    <AvatarFallback className="bg-blue-100 text-blue-600">
                      {contact.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    {/* Header row with title and timestamp */}
                    <div className="mb-1 flex items-start gap-2">
                      <h3
                        className="min-w-0 flex-1 text-sm font-medium leading-tight"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {contact.name}
                      </h3>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        {contact.isPinned && <Pin className="h-3 w-3 text-blue-600" />}
                        {contact.unreadCount != null && contact.unreadCount > 0 && (
                          <Badge variant="secondary" className="bg-blue-100 text-xs text-blue-700">
                            {contact.unreadCount}
                          </Badge>
                        )}
                        {contact.lastMessage && (
                          <span className="whitespace-nowrap text-xs text-gray-500">
                            {contact.lastMessage.timestamp}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Message preview */}
                    <p className="mb-1 truncate text-sm text-gray-500">
                      {contact.lastMessage?.content || "No messages yet"}
                    </p>

                    {/* Contact type badge and reference indicator */}
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                        {contact.type === "team"
                          ? "Team"
                          : contact.type === "client"
                            ? "Client"
                            : contact.type === "lead"
                              ? "Lead"
                              : contact.type === "prime-contractor"
                                ? "Prime Contractor"
                                : contact.type === "subcontractor"
                                  ? "Subcontractor"
                                  : contact.type === "business-contact"
                                    ? "Business Contact"
                                    : "Contact"}
                      </span>

                      {/* Reference indicator */}
                      {contact.reference &&
                        (() => {
                          const referenceInfo = getReferenceInfo(contact.reference.type);
                          if (!referenceInfo) return null;

                          const Icon = referenceInfo.icon;
                          return (
                            <Badge
                              variant="outline"
                              className={`${referenceInfo.color} h-5 px-1.5 py-0.5 text-xs`}
                            >
                              <Icon className="mr-1 h-3 w-3" />
                              {referenceInfo.label}
                            </Badge>
                          );
                        })()}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">No contacts found</div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
